import * as Schema from "effect/Schema";

/**
 * How long a Cloud Workspace may go without any activity before it is let go: a client steps away
 * after this long without its user, and the run then stops once its agent has also done no work
 * for this long. Short, since a running machine is what costs (ADR-0153): a user who comes back
 * starts it again in about a minute.
 */
export const CLOUD_UNUSED_WINDOW_MS = 10 * 60_000;
export const CloudRunClaimResponse = Schema.Struct({
  claimed: Schema.Literal(true),
});

/** An image AFAR builds and tests for Cloud Workspaces (ADR-0150). */
export const CloudImage = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  description: Schema.String,
  desktop: Schema.Boolean,
});
export type CloudImage = typeof CloudImage.Type;
/**
 * A machine size and what it gives. A Cloud Workspace can be rented at any size its provider sells
 * (ADR-0152); a Guest Environment picks from AFAR's own catalog.
 */
export const CloudSize = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  vcpu: Schema.Int,
  memoryGb: Schema.Int,
});
export type CloudSize = typeof CloudSize.Type;
export const CloudCatalog = Schema.Struct({
  images: Schema.Array(CloudImage),
  sizes: Schema.Array(CloudSize),
  disk: Schema.Struct({ minGb: Schema.Int, maxGb: Schema.Int, defaultGb: Schema.Int }),
});
export type CloudCatalog = typeof CloudCatalog.Type;

/**
 * A Cloud Workspace is a virtual machine on its owner's AFAR Network that runs only while it has
 * work (ADR-0150). `stopped` is its resting state; a rented machine is still charged while stopped
 * (ADR-0152). It is reached like any machine on the network, by its id, so it carries no endpoint.
 */
export const CloudWorkspace = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  image: Schema.String,
  size: Schema.String,
  diskGb: Schema.Int,
  state: Schema.Literals([
    "provisioning",
    "stopped",
    "starting",
    "running",
    "stopping",
    "deleting",
    "deleted",
  ]),
  error: Schema.NullOr(Schema.String),
  revision: Schema.Int,
  deleteAfter: Schema.NullOr(Schema.Number),
});
export type CloudWorkspace = typeof CloudWorkspace.Type;
export const CreateCloudWorkspace = Schema.Struct({
  requestId: Schema.String,
  label: Schema.String,
  image: Schema.String,
  size: Schema.String,
  diskGb: Schema.Int,
});
export type CreateCloudWorkspace = typeof CreateCloudWorkspace.Type;
/** A resize changes the size, grows the disk, or both, and is accepted only while stopped. */
export const CloudWorkspaceAction = Schema.Struct({
  id: Schema.String,
  expectedRevision: Schema.Int,
  requestId: Schema.String,
  size: Schema.optional(Schema.String),
  diskGb: Schema.optional(Schema.Int),
  action: Schema.Literals(["open", "stop", "delete", "resize"]),
});
export type CloudWorkspaceAction = typeof CloudWorkspaceAction.Type;

export const CloudPlan = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  /** The plan's one monthly usage limit in microdollars; administrators only. */
  monthlyAllowance: Schema.NullOr(Schema.Int),
  workspaceLimit: Schema.Int,
});
export type CloudPlan = typeof CloudPlan.Type;
export const CloudSubscriptionStatus = Schema.Literals(["active", "canceled"]);
export const CloudSubscription = Schema.Struct({
  plan: Schema.String,
  status: CloudSubscriptionStatus,
  revision: Schema.Int,
  periodStartsAt: Schema.Number,
  renewsAt: Schema.NullOr(Schema.Number),
});
export type CloudSubscription = typeof CloudSubscription.Type;

export const CloudAllowanceStatus = Schema.Struct({
  /** Share of the Cloud Plan's one monthly limit left; null without a plan. */
  usageLeftPercent: Schema.NullOr(Schema.Int),
  /** Exact amounts are for administrators only; accounts see usageLeftPercent. */
  balance: Schema.NullOr(Schema.Int),
  subscription: Schema.NullOr(CloudSubscription),
  plans: Schema.Array(CloudPlan),
  catalog: CloudCatalog,
  admin: Schema.Boolean,
  storageRetentionDays: Schema.Int,
  /**
   * Microdollar rates, administrators only: an hour of each rentable size, charged while its
   * machine exists, and a GB of disk for a day.
   */
  prices: Schema.NullOr(
    Schema.Struct({
      sizeHourly: Schema.Record(Schema.String, Schema.Int),
      diskGbDaily: Schema.Int,
    }),
  ),
});
export type CloudAllowanceStatus = typeof CloudAllowanceStatus.Type;

export const CloudAccountAllowance = Schema.Struct({
  id: Schema.String,
  /** The account's Google-verified email, or its id if it has none. */
  email: Schema.String,
  name: Schema.NullOr(Schema.String),
  balance: Schema.Int,
  consumed: Schema.Int,
  workspaces: Schema.Int,
  subscription: Schema.NullOr(
    Schema.Struct({ plan: Schema.String, status: CloudSubscriptionStatus }),
  ),
  subscriptionRevision: Schema.Int,
});
export type CloudAccountAllowance = typeof CloudAccountAllowance.Type;
export const CloudAllowanceHistory = Schema.Struct({
  requestId: Schema.String,
  actorId: Schema.String,
  /** The acting admin's email, or their account id if it has none. */
  actorEmail: Schema.String,
  operation: Schema.Literals(["grant", "revoke"]),
  amount: Schema.Int,
  requestedAmount: Schema.Int,
  note: Schema.String,
  at: Schema.Int,
});
export type CloudAllowanceHistory = typeof CloudAllowanceHistory.Type;
export const AdjustCloudAllowance = Schema.Struct({
  accountId: Schema.String,
  requestId: Schema.String,
  operation: Schema.Literals(["grant", "revoke"]),
  amount: Schema.Int,
  note: Schema.String,
});
export type AdjustCloudAllowance = typeof AdjustCloudAllowance.Type;
export const ChangeCloudSubscription = Schema.Struct({
  accountId: Schema.String,
  requestId: Schema.String,
  expectedRevision: Schema.Int,
  operation: Schema.Literals(["attach", "cancel"]),
  plan: Schema.NullOr(Schema.String),
  note: Schema.String,
});
export type ChangeCloudSubscription = typeof ChangeCloudSubscription.Type;
